/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1780876800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jun 08 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "partnerize",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Partnerize",
          "depth": 0,
          "id": "connect-to-partnerize"
        }, {
          "value": "Obtain API credentials",
          "depth": 1,
          "id": "obtain-api-credentials"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Campaign",
          "depth": 1,
          "id": "campaign"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Sync behavior",
          "depth": 1,
          "id": "sync-behavior"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Value is optional",
          "depth": 1,
          "id": "value-is-optional"
        }, {
          "value": "Required-field rejections",
          "depth": 1,
          "id": "required-field-rejections"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      ul: "ul",
      li: "li",
      h3: "h3",
      h4: "h4",
      code: "code",
      em: "em"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Partnerize is a global partnership and affiliate marketing platform that helps brands track conversions, attribute them to publishers, and manage commissions. With the Partnerize destination, you can sync conversion (sale) events from your data warehouse to Partnerize using its server-to-server (S2S) conversion tracking, enabling accurate affiliate attribution without relying on browser-side pixels."
      }), "\n", _jsx(_components.p, {
        children: "Conversions are recorded against a specific Partnerize campaign and support single- or multi-item orders, basket-level attributes, and vertical-specific custom parameters."
      }), "\n", _jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion (sale) events from any source to Partnerize"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://api-docs.partnerize.com/partner/",
                children: "Partnerize API docs"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-partnerize",
        children: "Connect to Partnerize"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Partnerize"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "You need to provide the following credentials:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User Application Key"
          }), ": The application key for your Partnerize user."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User API Key"
          }), ": The API key for your Partnerize user."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Together these authenticate Hightouch with the Partnerize API (HTTP Basic authentication), which Hightouch uses to validate your connection and load the list of campaigns you can sync to."
      }), "\n", _jsx(_components.h3, {
        id: "obtain-api-credentials",
        children: "Obtain API credentials"
      }), "\n", _jsxs(_components.p, {
        children: ["In Partnerize, go to ", _jsx(_components.strong, {
          children: "Settings → Account Settings → API"
        }), " to find your ", _jsx(_components.strong, {
          children: "User Application Key"
        }), " and ", _jsx(_components.strong, {
          children: "User API Key"
        }), ". The user associated with these keys must have access to the campaigns you want to track conversions against."]
      }), "\n", _jsx(Warning, {
        children: _jsx(_components.p, {
          children: "Test the connection before creating syncs. Hightouch validates your keys against the Partnerize API and confirms it can list your campaigns; if either check fails, double-check your User Application Key and User API Key."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Partnerize destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Partnerize destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "campaign",
        children: "Campaign"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "Partnerize campaign"
        }), " that conversions should be tracked against. Hightouch loads the campaigns available to your account from the Partnerize API. Every conversion in the sync is recorded against the selected campaign."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you sync conversion fields via ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "field mapping"
        }), ". You can map data from any of your model columns to Partnerize's conversion fields."]
      }), "\n", _jsx(_components.h4, {
        id: "required-fields",
        children: "Required fields"
      }), "\n", _jsx(_components.p, {
        children: "You must map the following fields to complete your configuration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Conversion reference (order ID)"
          }), ": Your unique reference for the conversion — typically the order ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Click reference"
          }), ": The click reference captured at the point of sale. It must match the originating click for Partnerize to attribute the conversion."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["In addition, every conversion must include ", _jsx(_components.strong, {
          children: "at least one item"
        }), " with a ", _jsx(_components.strong, {
          children: "category"
        }), " and a ", _jsx(_components.strong, {
          children: "quantity"
        }), " (see ", _jsx(_components.a, {
          href: "#items",
          children: "Items"
        }), " below). These item-level fields are required by Partnerize's S2S specification, but they're enforced when the conversion is sent rather than as required mappings, because items can also be supplied as an array."]
      }), "\n", _jsx(_components.h4, {
        id: "items",
        children: "Items"
      }), "\n", _jsx(_components.p, {
        children: "A conversion's line items can be provided in two ways:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Single item"
          }), " — map the top-level ", _jsx(_components.strong, {
            children: "Item category"
          }), ", ", _jsx(_components.strong, {
            children: "Item quantity"
          }), ", ", _jsx(_components.strong, {
            children: "Item value"
          }), ", and ", _jsx(_components.strong, {
            children: "Item SKU"
          }), " fields. This describes one line item."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Multiple items"
          }), " — map a column containing an ", _jsx(_components.strong, {
            children: "array"
          }), " of line items using the ", _jsx(_components.a, {
            href: "/syncs/mapping-data#inline-mapping",
            children: "inline mapper"
          }), ". Each entry takes the shape ", _jsx(_components.code, {
            children: "{ category, sku, value, quantity }"
          }), ". When mapped and non-empty, the items array takes precedence over the single-item fields."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For each item:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "category"
          }), " ", _jsx(_components.em, {
            children: "(required)"
          }), " — the item category."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "quantity"
          }), " ", _jsx(_components.em, {
            children: "(required)"
          }), " — the number of units."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "value"
          }), " ", _jsx(_components.em, {
            children: "(optional)"
          }), " — the item value, excluding tax and currency symbols. Only required when the campaign awards percentage-based commission."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "sku"
          }), " ", _jsx(_components.em, {
            children: "(optional)"
          }), " — the product SKU."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "optional-basket-level-fields",
        children: "Optional basket-level fields"
      }), "\n", _jsx(_components.p, {
        children: "These describe the order as a whole and are omitted from the conversion when left unmapped:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Currency"
          }), " — ISO 4217 currency code (for example, ", _jsx(_components.code, {
            children: "USD"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Country"
          }), " — ISO 3166 country code (for example, ", _jsx(_components.code, {
            children: "US"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Customer type"
          }), " — ", _jsx(_components.code, {
            children: "new"
          }), " or ", _jsx(_components.code, {
            children: "existing"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Voucher code"
          }), " — the voucher or discount code used on the order."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Customer reference"
          }), " — your own identifier for the customer."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Fulfilment date"
          }), " — ISO 8601 ", _jsx(_components.code, {
            children: "YYYY-MM-DD"
          }), "; drives Partnerize's auto-approval window."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "custom-parameters",
        children: "Custom parameters"
      }), "\n", _jsxs(_components.p, {
        children: ["Use ", _jsx(_components.a, {
          href: "/syncs/mapping-data",
          children: "custom mappings"
        }), " to send additional, vertical-specific basket parameters (for example, ", _jsx(_components.code, {
          children: "check_in_date"
        }), " for travel). Each custom mapping is appended to the conversion as an extra parameter."]
      }), "\n", _jsx(_components.h3, {
        id: "sync-behavior",
        children: "Sync behavior"
      }), "\n", _jsx(_components.p, {
        children: "Partnerize conversions are recorded server-to-server through Partnerize's conversion tracking host. For each row in your model, Hightouch:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Builds a conversion from the mapped fields and validates the required fields before sending."
        }), "\n", _jsx(_components.li, {
          children: "Sends one tracking request per conversion (requests within a batch are sent concurrently)."
        }), "\n", _jsxs(_components.li, {
          children: ["Records the conversion against the selected campaign. A successful send returns ", _jsx(_components.code, {
            children: "200 OK"
          }), ", and the conversion then enters a ", _jsx(_components.code, {
            children: "pending"
          }), " state in Partnerize for downstream validation and approval."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Partnerize's conversion tracking host accepts a request and queues it for validation rather than rejecting incomplete data at send time. To keep your data clean, Hightouch validates the documented required fields — conversion reference, click reference, and per-item category and quantity — ", _jsx(_components.strong, {
            children: "before"
          }), " sending. Rows that don't satisfy them are rejected and never sent."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "value-is-optional",
        children: "Value is optional"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.code, {
          children: "value"
        }), " is only required when a campaign awards percentage-based commission. A conversion with a category and quantity but no value is valid and will be sent; the value is simply omitted from the request."]
      }), "\n", _jsx(_components.h3, {
        id: "required-field-rejections",
        children: "Required-field rejections"
      }), "\n", _jsx(_components.p, {
        children: "Rows are rejected (and not sent) when a required field is missing. The rejection message names the missing field:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Missing conversion reference → ", _jsx(_components.em, {
            children: "\"A conversion reference (order ID) is required for every Partnerize conversion.\""
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Missing click reference → ", _jsx(_components.em, {
            children: "\"A click reference is required to attribute a Partnerize conversion.\""
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["Item missing a category or quantity → ", _jsx(_components.em, {
            children: "\"Conversion item is missing required field(s): …\""
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["No item fields mapped at all → ", _jsx(_components.em, {
            children: "\"Each conversion must include at least one item with a category and quantity.\""
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "authentication-errors",
        children: "Authentication errors"
      }), "\n", _jsx(_components.p, {
        children: "If the connection test or campaign list fails with a 401 or 403:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Verify your ", _jsx(_components.strong, {
            children: "User Application Key"
          }), " and ", _jsx(_components.strong, {
            children: "User API Key"
          }), " are correct and entered in the right fields."]
        }), "\n", _jsx(_components.li, {
          children: "Confirm the user has access to the campaigns you want to track conversions against."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "conversions-not-appearing",
        children: "Conversions not appearing"
      }), "\n", _jsx(_components.p, {
        children: "If a sync succeeds but conversions don't appear as expected in Partnerize:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Confirm the ", _jsx(_components.strong, {
            children: "click reference"
          }), " matches a real click for the selected campaign — conversions without a matching click can't be attributed."]
        }), "\n", _jsxs(_components.li, {
          children: ["Remember that received conversions enter a ", _jsx(_components.code, {
            children: "pending"
          }), " state and are validated by Partnerize after they're sent."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
